
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6">
          <img 
            src="https://public.readdy.ai/ai/img_res/23f39a9d-841b-4bac-9739-30397fb9c8c0.png" 
            alt="JOLDOMERC d.o.o." 
            className="h-12 w-auto"
          />
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-800 to-cyan-600"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 text-center px-8 max-w-5xl mx-auto">
          <h1 className="text-7xl md:text-8xl font-bold text-white mb-6 leading-tight tracking-tight">
            Crafting Premium<br />iOS Experiences
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Expert development team specializing in VPN and Cleaner applications for enhanced mobile security
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-zinc-950 rounded-3xl p-20">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
              <div className="lg:col-span-3">
                <div className="text-xs uppercase tracking-widest text-gray-400 mb-6">• ABOUT US</div>
                <h2 className="text-5xl font-serif text-white mb-8 leading-tight">
                  Expert Team Delivering Excellence
                </h2>
                <p className="text-lg text-white/70 leading-relaxed max-w-xl">
                  JOLDOMERC d.o.o. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We combine cutting-edge technology with intuitive design to deliver solutions that users trust and rely on daily.
                </p>
              </div>
              <div className="lg:col-span-2">
                <img 
                  src="https://public.readdy.ai/ai/img_res/febeef2777885aefd2f36dc2750736b5.jpg"
                  alt="Development workspace"
                  className="w-full h-full object-cover rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-32 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-4">• OUR PRODUCTS</div>
            <h2 className="text-6xl font-bold text-gray-900 mb-6">Premium iOS Applications</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Innovative solutions designed to protect and optimize your mobile experience
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* VPN Product */}
            <div className="group relative bg-gradient-to-br from-indigo-900 via-purple-900 to-purple-800 rounded-2xl overflow-hidden h-[600px] cursor-pointer transition-all duration-500 hover:scale-[1.02]">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <img 
                src="https://readdy.ai/api/search-image?query=Premium%20VPN%20mobile%20application%20interface%20on%20iPhone%20showing%20secure%20connection%20status%20with%20shield%20icon%20and%20encrypted%20network%20visualization%2C%20modern%20iOS%20design%20with%20dark%20purple%20gradient%20background%2C%20clean%20minimalist%20UI%20with%20connection%20toggle%20and%20server%20selection%2C%20professional%20security%20app%20aesthetic&width=800&height=1000&seq=vpn-product-interface&orientation=portrait"
                alt="VPN Protection"
                className="w-full h-full object-cover object-top opacity-90"
              />
              <div className="absolute bottom-0 left-0 right-0 p-12">
                <h3 className="text-4xl font-bold text-white mb-4">VPN Protection</h3>
                <p className="text-lg text-white/90 leading-relaxed max-w-md">
                  Military-grade encryption securing your connection across all networks. Browse privately and access content safely from anywhere in the world.
                </p>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="group relative bg-gradient-to-br from-cyan-500 via-blue-500 to-blue-600 rounded-2xl overflow-hidden h-[600px] cursor-pointer transition-all duration-500 hover:scale-[1.02]">
              <div className="absolute top-6 left-6 z-10">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-semibold">
                  <i className="ri-star-fill"></i>
                  POPULAR
                </span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <img 
                src="https://readdy.ai/api/search-image?query=Smart%20Cleaner%20iOS%20application%20interface%20on%20iPhone%20displaying%20storage%20optimization%20dashboard%20with%20circular%20progress%20indicators%20showing%20freed%20space%2C%20clean%20white%20and%20blue%20gradient%20design%2C%20modern%20minimalist%20UI%20with%20file%20categories%20and%20cleanup%20suggestions%2C%20professional%20utility%20app%20aesthetic&width=800&height=1000&seq=cleaner-product-interface&orientation=portrait"
                alt="Smart Cleaner"
                className="w-full h-full object-cover object-top opacity-90"
              />
              <div className="absolute bottom-0 left-0 right-0 p-12">
                <h3 className="text-4xl font-bold text-white mb-4">Smart Cleaner</h3>
                <p className="text-lg text-white/90 leading-relaxed max-w-md">
                  Intelligent storage optimization and junk file removal. Keep your device running smoothly with automated cleaning and performance monitoring.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-8 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 mb-32">
            <div className="lg:col-span-2">
              <h2 className="text-7xl font-serif text-gray-900 leading-none">
                Built for<br />Modern iOS
              </h2>
            </div>
            <div className="lg:col-span-3 flex items-end">
              <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                Our applications leverage the latest iOS frameworks and design principles, ensuring seamless integration with Apple's ecosystem while maintaining exceptional performance standards.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <div className="bg-zinc-900 rounded-3xl p-12 aspect-[3/4]">
              <div className="flex flex-col h-full">
                <div className="mb-8">
                  <div className="w-20 h-20 flex items-center justify-center bg-white/10 rounded-2xl mb-6">
                    <i className="ri-shield-check-line text-4xl text-white"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-4">Security First</h3>
                  <p className="text-white/85 text-lg leading-relaxed">
                    End-to-end encryption protocols protecting user data at every touchpoint with industry-leading security standards.
                  </p>
                </div>
                <div className="mt-auto">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20digital%20security%20and%20encryption%20with%20geometric%20shield%20patterns%20and%20network%20connections%2C%20dark%20background%20with%20glowing%20blue%20and%20purple%20elements%2C%20minimalist%20tech%20illustration%20representing%20data%20protection%20and%20cybersecurity%2C%20modern%20graphic%20design%20aesthetic&width=400&height=400&seq=security-feature-visual&orientation=squarish"
                    alt="Security visualization"
                    className="w-full h-48 object-cover rounded-xl opacity-80"
                  />
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-stone-100 rounded-3xl p-12 aspect-[3/4]">
              <div className="flex flex-col h-full">
                <div className="mb-6">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-full text-gray-900 text-xs font-semibold uppercase tracking-wide">
                    <i className="ri-flashlight-fill"></i>
                    OPTIMIZED
                  </span>
                </div>
                <div className="mb-8">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Intelligent Algorithms</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Machine learning-powered optimization delivering measurable performance improvements and efficient resource management.
                  </p>
                </div>
                <div className="mt-auto">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Clean%20iOS%20app%20interface%20mockup%20showing%20performance%20analytics%20dashboard%20with%20smooth%20gradient%20charts%20and%20optimization%20metrics%2C%20light%20background%20with%20blue%20and%20purple%20accent%20colors%2C%20modern%20minimalist%20design%20with%20data%20visualization%20elements%2C%20professional%20mobile%20UI%20aesthetic&width=400&height=400&seq=optimization-feature-visual&orientation=squarish"
                    alt="Optimization interface"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-stone-100 rounded-3xl p-12 aspect-[3/4]">
              <div className="flex flex-col h-full">
                <div className="mb-6">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full text-gray-900 text-xs font-semibold uppercase tracking-wide">
                    <i className="ri-checkbox-circle-fill"></i>
                    VERIFIED
                  </span>
                </div>
                <div className="mb-8">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Privacy Focused</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Zero-log policy with transparent data handling practices. Your privacy is our priority, always.
                  </p>
                </div>
                <div className="mt-auto">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Privacy%20and%20data%20protection%20concept%20with%20lock%20icons%20and%20encrypted%20data%20streams%2C%20clean%20light%20background%20with%20subtle%20blue%20tones%2C%20minimalist%20illustration%20showing%20secure%20information%20flow%20and%20user%20privacy%20protection%2C%20modern%20tech%20graphic%20design%20aesthetic&width=400&height=400&seq=privacy-feature-visual&orientation=squarish"
                    alt="Privacy visualization"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-black rounded-3xl p-20">
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-16">
              {/* Contact Info */}
              <div className="lg:col-span-4">
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-6">• GET IN TOUCH</div>
                <h2 className="text-5xl font-serif text-white mb-12 leading-tight">
                  Contact Us
                </h2>
                
                <div className="space-y-8">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">EMAIL</div>
                    <div className="flex items-center gap-3 text-white text-lg">
                      <i className="ri-mail-line text-2xl"></i>
                      <span>info@joldomercdoo.com</span>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">PHONE</div>
                    <div className="flex items-center gap-3 text-white text-lg">
                      <i className="ri-phone-line text-2xl"></i>
                      <span>+1 2314032524</span>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">ADDRESS</div>
                    <div className="flex items-start gap-3 text-white text-lg leading-relaxed">
                      <i className="ri-map-pin-line text-2xl mt-1"></i>
                      <div>
                        <div>Donji Zovik 20</div>
                        <div>Sarajevo, 71000</div>
                        <div>Bosnia &amp; Herzegovina</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Visual */}
              <div className="lg:col-span-6">
                <div className="w-full h-full min-h-[500px] rounded-2xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2876.4!2d18.4131!3d43.8564!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDUxJzIzLjAiTiAxOMKwMjQnNDcuMiJF!5e0!3m2!1sen!2s!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="JOLDOMERC d.o.o. Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="border-t border-gray-200 pt-12">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="text-gray-600 text-sm">
                &copy; 2025 JOLDOMERC d.o.o. All rights reserved
              </div>
              <div className="text-gray-600 text-sm">
                <a 
                  href="https://readdy.ai/?ref=logo" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-gray-900 transition-colors cursor-pointer"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
